import React, { useEffect, useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import AppLayout from "@/components/layout/app-layout";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { FolderClosed, Receipt } from "lucide-react";
import { Tenant } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { useAuthenticatedRequest } from "@/hooks/use-authenticated-request";
import { formatCurrency, getStatusBadgeClass } from "@/lib/utils";
import { useTenantCurrency } from "@/hooks/use-tenant";
import { formatCurrencyWithTenantAndConversion } from "@/lib/currency-conversion";

interface DashboardProject {
  projectId: string;
  projectName: string;
  status: string;
  contractAmount: number;
  contractCurrency: string;
  activeClaimsCount: number;
  activeClaimsAmount: number;
  settledClaimsCount: number;
  settledClaimsAmount: number;
  claimCurrency: string;
}

const HomePage = React.memo(function HomePage() {
  const { user } = useAuth();
  const authenticatedRequest = useAuthenticatedRequest();
  const { toast } = useToast();
  const { currency: tenantCurrency } = useTenantCurrency();

  console.log("[HomePage] Rendering with user:", user?.email, "ActiveContext:", user?.activeTenantContext);

  // Memoize dashboard metrics query function
  const metricsQueryFn = useMemo(() => async () => {
    const response = await authenticatedRequest("/api/dashboard/metrics");
    if (!response.ok) {
      throw new Error("Network response was not ok for dashboard metrics");
    }
    return response.json();
  }, [authenticatedRequest]);

  const { 
    data: metrics, 
    error: metricsError
  } = useQuery({
    queryKey: ["/api/dashboard/metrics", user?.activeTenantContext],
    queryFn: metricsQueryFn,
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
    retry: 1,
    retryDelay: 1000
  });
  
  // Log any metrics errors
  useEffect(() => {
    if (metricsError) {
      console.error("[HomePage] Error fetching metrics:", metricsError);
      toast({
        title: "Error loading metrics",
        description: "Please try refreshing the page",
        variant: "destructive",
      });
    }
  }, [metricsError, toast]);

  // Memoize dashboard projects query function
  const dashboardProjectsQueryFn = useMemo(() => async () => {
    const response = await authenticatedRequest("/api/dashboard/projects");
    if (!response.ok) {
      throw new Error("Network response was not ok for dashboard projects");
    }
    return response.json();
  }, [authenticatedRequest]);

  const { 
    data: projects = [], 
    isLoading: isLoadingProjects,
    error: projectsError
  } = useQuery({
    queryKey: ["/api/dashboard/projects", user?.activeTenantContext],
    queryFn: dashboardProjectsQueryFn,
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
    retry: 1,
    retryDelay: 1000
  });
  
  // Log any projects errors
  useEffect(() => {
    if (projectsError) {
      console.error("[HomePage] Error fetching dashboard projects:", projectsError);
      toast({
        title: "Error loading projects",
        description: "Please try refreshing the page",
        variant: "destructive",
      });
    }
  }, [projectsError, toast]);
  
  // Memoize tenant query function
  const tenantQueryFn = useMemo(() => async () => {
    // Don't fetch tenant details if in "All Tenants" mode or no tenant selected
    if (user?.activeTenantContext === "__ALL__" || !user?.activeTenantContext || user?.activeTenantContext === "__NULL__") {
      return null;
    }

    const response = await authenticatedRequest('/api/tenant/settings');
    if (!response.ok) {
      throw new Error("Network response was not ok for tenant management");
    }
    return response.json();
  }, [user?.activeTenantContext, authenticatedRequest]);

  // Fetch tenant details
  const { 
    data: _tenant,
    error: tenantError
  } = useQuery<Tenant>({
    queryKey: ["/api/tenant/settings", user?.activeTenantContext],
    queryFn: tenantQueryFn,
    enabled: !!user && user?.activeTenantContext !== "__ALL__" && !!user?.activeTenantContext && user?.activeTenantContext !== "__NULL__",
    retry: 1,
    retryDelay: 1000
  });

  // Notify about any errors on component mount
  useEffect(() => {
    if (tenantError) {
      console.warn("[HomePage] Tenant error:", tenantError);
    }
  }, [tenantError, toast]);

  // Default metrics structure
  const defaultMetrics = {
    projects: {
      total: { count: 0, amount: 0, currency: 'USD' },
      active: { count: 0, amount: 0, currency: 'USD' },
      completed: { count: 0, amount: 0, currency: 'USD' }
    },
    claims: {
      total: { count: 0, amount: 0, currency: 'USD' },
      active: { count: 0, amount: 0, currency: 'USD' },
      settled: { count: 0, amount: 0, currency: 'USD' }
    }
  };
  
  const currentMetrics = metrics || defaultMetrics;

  if (!user) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <p>Please log in to access the dashboard.</p>
        </div>
      </AppLayout>
    );
  }

  // Tenant name display logic
  // const tenantName = user.isSuperAdmin 
  //   ? (user.activeTenantContext === "__ALL__" ? "All Tenants" : tenant?.companyName || "Select Tenant")
  //   : tenant?.companyName || "Loading...";

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back, {user.fullName || user.email}! 
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Project Metrics */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
              <FolderClosed className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{currentMetrics.projects.total.count}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrencyWithTenantAndConversion(currentMetrics.projects.total.amount, currentMetrics.projects.total.currency, tenantCurrency, {
                  showSymbol: true,
                  showOriginal: false,
                  showConversionRate: false
                })} total value
                {currentMetrics.projects.total.currency !== tenantCurrency && (
                  <span className="text-blue-600 ml-1">💱 converted</span>
                )}
              </p>
              <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                <span>Active: {currentMetrics.projects.active.count} ({formatCurrencyWithTenantAndConversion(currentMetrics.projects.active.amount, currentMetrics.projects.active.currency, tenantCurrency, {
                  showSymbol: true, showOriginal: false, showConversionRate: false
                })})</span>
                <span>Complete: {currentMetrics.projects.completed.count} ({formatCurrencyWithTenantAndConversion(currentMetrics.projects.completed.amount, currentMetrics.projects.completed.currency, tenantCurrency, {
                  showSymbol: true, showOriginal: false, showConversionRate: false
                })})</span>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Claims</CardTitle>
              <Receipt className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{currentMetrics.claims.total.count}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrencyWithTenantAndConversion(currentMetrics.claims.total.amount, currentMetrics.claims.total.currency, tenantCurrency, {
                  showSymbol: true,
                  showOriginal: false,
                  showConversionRate: false
                })} total amount
                {currentMetrics.claims.total.currency !== tenantCurrency && (
                  <span className="text-blue-600 ml-1">💱 converted</span>
                )}
              </p>
              <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                <span>Active: {currentMetrics.claims.active.count} ({formatCurrencyWithTenantAndConversion(currentMetrics.claims.active.amount, currentMetrics.claims.active.currency, tenantCurrency, {
                  showSymbol: true, showOriginal: false, showConversionRate: false
                })})</span>
                <span>Settled: {currentMetrics.claims.settled.count} ({formatCurrencyWithTenantAndConversion(currentMetrics.claims.settled.amount, currentMetrics.claims.settled.currency, tenantCurrency, {
                  showSymbol: true, showOriginal: false, showConversionRate: false
                })})</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Projects Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Projects</CardTitle>
                <CardDescription>
                  Your most recently created projects with claim details
                </CardDescription>
              </div>
              <Button size="sm" asChild>
                <Link href="/projects/new/edit">
                  <FolderClosed className="h-4 w-4 mr-2" />
                  Create Project
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingProjects ? (
              <div className="text-center py-8">
                <p>Loading projects...</p>
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-8">
                <FolderClosed className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium">No projects yet</p>
                <p className="text-muted-foreground mb-4">Get started by creating your first project</p>
                <Button asChild>
                  <Link href="/projects/new/edit">Create Project</Link>
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-neutral-800 sm:pl-6">
                        Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-800">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-800 hidden sm:table-cell">
                        Contract Amount
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-800 hidden md:table-cell">
                        Active Claims
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-800 hidden lg:table-cell">
                        Settled Claims
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200 bg-white">
                    {projects.map((project: DashboardProject) => (
                      <tr 
                        key={project.projectId} 
                        className="hover:bg-neutral-50 cursor-pointer"
                        onClick={() => window.location.href = `/projects/${project.projectId}`}
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-neutral-800 sm:pl-6">
                          {project.projectName}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span className={getStatusBadgeClass(project.status)}>
                            {project.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-600 hidden sm:table-cell">
                          <div>
                            {formatCurrencyWithTenantAndConversion(project.contractAmount, project.contractCurrency, tenantCurrency, {
                              showSymbol: true,
                              showOriginal: false,
                              showConversionRate: false
                            })}
                            {project.contractCurrency !== tenantCurrency && (
                              <div className="text-xs text-blue-600">💱 {project.contractCurrency}</div>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-600 hidden md:table-cell">
                          <div>
                            {project.activeClaimsCount} ({formatCurrencyWithTenantAndConversion(project.activeClaimsAmount, project.claimCurrency, tenantCurrency, {
                              showSymbol: true, showOriginal: false, showConversionRate: false
                            })})
                            {project.claimCurrency !== tenantCurrency && (
                              <div className="text-xs text-blue-600">💱 {project.claimCurrency}</div>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-600 hidden lg:table-cell">
                          <div>
                            {project.settledClaimsCount} ({formatCurrencyWithTenantAndConversion(project.settledClaimsAmount, project.claimCurrency, tenantCurrency, {
                              showSymbol: true, showOriginal: false, showConversionRate: false
                            })})
                            {project.claimCurrency !== tenantCurrency && (
                              <div className="text-xs text-blue-600">💱 {project.claimCurrency}</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

    </AppLayout>
  );
});

export default HomePage;
